import { useEffect, useMemo, useRef, useState } from "react";
import { useAccessibleOrganizationsTree } from "../itCompliance/hooks";
import { fetchCmdbToolsByOrg, type CmdbToolByOrgInstance } from "../itCompliance/services/cmdbToolsApi";
import { collectOrgScopeByType } from "../itCompliance/utils/scopeTree";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../../components/Modal";
import { DiscardConfirmDialog, useDiscardGuard } from "../../components/DiscardConfirmDialog";
import { DateTimePicker } from "../../components/ui/DateTimePicker";
import { Dropdown } from "../../components/Dropdown";
import { WeekdayMultiSelect } from "./WeekdayMultiSelect";
import {
  FREQUENCY_OPTIONS,
  type AddDataImportPayload,
  type SelectItem,
} from "./schedulerTypes";
import { normalizeKey } from "./schedulerMappers";

interface Props {
  open: boolean;
  mode?: "add" | "edit";
  initial?: AddDataImportPayload;
  /** Pins Module to a single value (read-only field instead of a dropdown). */
  fixedModule?: SelectItem;
  /** Explicitly restricts tool options for this scheduler modal. */
  allowedTools?: SelectItem[];
  /** Custom tool options (e.g. for PGA Scheduler). Falls back to TOOL_OPTIONS if omitted. */
  toolOptions?: SelectItem[];
  onClose: () => void;
  onSave: (payload: AddDataImportPayload) => void;
}

const MODULE_OPTIONS: SelectItem[] = [
  { value: "vulnerability_management", label: "Vulnerability Management" },
  { value: "configuration_management", label: "Configuration Management" },
  { value: "stoc", label: "Security Tools Operations Center" },
];

/** Transform API tool instances to SelectItem options for the dropdown. */
const transformToolsToOptions = (tools: CmdbToolByOrgInstance[]): SelectItem[] => {
  const seen = new Set<string>();
  return tools
    .map((t) => {
      if (seen.has(t.toolName)) return null;
      seen.add(t.toolName);
      return { value: t.toolName, label: t.toolName };
    })
    .filter((o): o is SelectItem => o !== null);
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Local-date ISO (`YYYY-MM-DD`) — avoids the UTC shift of `toISOString`. */
const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const EMPTY: AddDataImportPayload = {
  module: "",
  tool: "",
  instanceName: "",
  environment: "",
  importFrequency: "",
  importOn: "",
  importAt: "00:00",
};

/**
 * Add / Edit a data-import schedule (module + tool + frequency/on/at). Mirrors
 * g360's `AddDataImportModal`.
 */
export function AddDataImportModal({
  open,
  mode = "add",
  initial,
  fixedModule,
  allowedTools,
  toolOptions: customToolOptions,
  onClose,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<AddDataImportPayload>(initial ?? EMPTY);
  const [errors, setErrors] = useState<
    Partial<Record<keyof AddDataImportPayload, string>>
  >({});
  const [toolInstances, setToolInstances] = useState<CmdbToolByOrgInstance[]>([]);
  const [fetchedToolOptions, setFetchedToolOptions] = useState<SelectItem[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  const needsScopeTree =
    open &&
    !((allowedTools && allowedTools.length > 0) || (customToolOptions && customToolOptions.length > 0));
  const scopeTree = useAccessibleOrganizationsTree({ enabled: needsScopeTree });

  const isStocModule = draft.module === "stoc" || fixedModule?.value === "stoc";
  const activeToolOptions = useMemo(() => {
    if (customToolOptions && customToolOptions.length > 0) return customToolOptions;
    if (allowedTools && allowedTools.length > 0) return allowedTools;
    return fetchedToolOptions;
  }, [customToolOptions, allowedTools, fetchedToolOptions]);

  const matchedToolValue = useMemo(() => {
    if (!draft.tool) return "";
    const direct = activeToolOptions.find((o) => o.value === draft.tool);
    if (direct) return direct.value;
    const byNormalized = activeToolOptions.find(
      (o) =>
        normalizeKey(o.value) === normalizeKey(draft.tool) ||
        normalizeKey(o.label) === normalizeKey(draft.tool),
    );
    return byNormalized ? byNormalized.value : draft.tool;
  }, [draft.tool, activeToolOptions]);

  // When tool changes, compute environment options.
  // normalizeKey is used because draft.tool is stored as a normalized key (e.g. "tenable_io")
  // while toolInstances carry the raw API toolName (e.g. "Tenable IO").
  // When editing, we also seed the current draft.environment so the dropdown shows the saved
  // value immediately while toolInstances are still being fetched.
  const environmentOptions = useMemo(() => {
    if (!draft.tool) return [];
    const normDraftTool = normalizeKey(draft.tool);
    const instances = toolInstances.filter(
      (t) => normalizeKey(t.toolName) === normDraftTool,
    );
    const seen = new Set<string>();
    const opts = instances
      .map((t) => t.environment)
      .filter((env): env is string => {
        if (!env || seen.has(env)) return false;
        seen.add(env);
        return true;
      })
      .map((env) => ({ value: env, label: env }));
    // Preserve the pre-filled environment value while instances are still loading.
    if (draft.environment && !seen.has(draft.environment)) {
      opts.unshift({ value: draft.environment, label: draft.environment });
    }
    return opts;
  }, [draft.tool, draft.environment, toolInstances]);

  // When environment changes, auto-populate instanceName
  const selectedInstance = useMemo(() => {
    if (!draft.tool || !draft.environment) return null;
    const normDraftTool = normalizeKey(draft.tool);
    return toolInstances.find(
      (t) =>
        normalizeKey(t.toolName) === normDraftTool &&
        t.environment === draft.environment,
    );
  }, [draft.tool, draft.environment, toolInstances]);

  // Update instanceName when environment is selected
  useEffect(() => {
    if (selectedInstance) {
      setDraft((prev) => ({ ...prev, instanceName: selectedInstance.instanceName }));
    }
  }, [selectedInstance]);

  // Fetch tools when modal opens (and scope tree is ready), unless tools are explicitly provided via allowedTools.
  // Reset the form to the current initial payload each time the modal opens.
  const openedRef = useRef(false);
  const initialRef = useRef<AddDataImportPayload>(initial ?? EMPTY);
  useEffect(() => {
    if (!open) {
      openedRef.current = false;
      return;
    }
    if (!openedRef.current) {
      openedRef.current = true;
      const withModule = fixedModule ? { ...(initial ?? EMPTY), module: fixedModule.value } : initial ?? EMPTY;
      // Monthly auto-selects the current day when none is set yet.
      const starting =
        withModule.importFrequency === "Monthly" && !withModule.importOn
          ? { ...withModule, importOn: todayIso() }
          : withModule;
      setDraft(starting);
      initialRef.current = starting;
      setErrors({});
    }
    if ((allowedTools && allowedTools.length > 0) || (customToolOptions && customToolOptions.length > 0)) return;
    if (scopeTree.loading) return;
    let cancelled = false;
    setToolsLoading(true);
    fetchCmdbToolsByOrg(collectOrgScopeByType(scopeTree.data))
      .then((instances) => {
        if (cancelled) return;
        setToolInstances(instances);
        setFetchedToolOptions(transformToolsToOptions(instances));
      })
      .catch((err) => {
        console.error("Failed to fetch tools for data import:", err);
      })
      .finally(() => {
        if (!cancelled) setToolsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, initial, fixedModule, allowedTools, customToolOptions, scopeTree.loading, scopeTree.data]);

  const update = <K extends keyof AddDataImportPayload>(
    key: K,
    value: AddDataImportPayload[K],
  ) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "importFrequency") {
        // Monthly auto-selects the current day; Daily is fixed; Weekly clears.
        next.importOn =
          value === "Daily" ? "Daily" : value === "Monthly" ? todayIso() : "";
      }
      if (key === "module") {
        next.tool = "";
        next.environment = "";
        next.instanceName = "";
      }
      if (key === "tool") {
        next.environment = "";
        next.instanceName = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initialRef.current),
    [draft],
  );

  const { requestClose, confirmProps } = useDiscardGuard(isDirty, onClose);

  const handleSave = () => {
    const next: typeof errors = {};
    if (!draft.module) next.module = "Module is required";
    if (!draft.tool) next.tool = "Tool is required";
    if (isStocModule) {
      if (!draft.environment) next.environment = "Environment is required";
      if (!draft.instanceName) next.instanceName = "Instance Name is required";
    }
    if (!draft.importFrequency) next.importFrequency = "Import Frequency is required";
    if (draft.importFrequency && draft.importFrequency !== "Daily" && !draft.importOn.trim()) {
      next.importOn = "Import On is required";
    }
    if (!draft.importAt) next.importAt = "Import At is required";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSave({
      ...draft,
      tool: matchedToolValue || draft.tool,
    });
  };

  const minDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (mode === "edit" && initial?.importOn) {
      const existing = new Date(initial.importOn);
      if (!Number.isNaN(existing.getTime()) && existing < today) {
        existing.setHours(0, 0, 0, 0);
        return existing;
      }
    }
    return today;
  }, [mode, initial?.importOn]);

  const renderImportOn = () => {
    if (draft.importFrequency === "Weekly") {
      return (
        <WeekdayMultiSelect value={draft.importOn} onChange={(v) => update("importOn", v)} />
      );
    }
    if (draft.importFrequency === "Monthly") {
      return (
        <DateTimePicker
          variant="single"
          valueType="string"
          value={draft.importOn || undefined}
          onValueChange={(v) => update("importOn", v ?? "")}
          placeholder="DD/MM/YYYY"
          displayFormat="dd/MM/yyyy"
          minDate={minDate}
          clearable
        />
      );
    }
    if (draft.importFrequency === "Daily") {
      return <FormInput value="Daily" disabled />;
    }
    return <FormInput value={draft.importOn} disabled />;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        title={mode === "edit" ? "Edit Schedule" : "Add Schedule"}
        footer={
          <ModalFooterButtons
            onCancel={requestClose}
            onSubmit={handleSave}
            submitLabel={mode === "edit" ? "Update" : "Save"}
            submitDisabled={!isDirty}
          />
        }
      >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Module" required error={errors.module}>
          {fixedModule ? (
            <FormInput value={fixedModule.label} disabled />
          ) : (
            <FormSelect
              value={draft.module}
              onChange={(v) => update("module", v)}
              placeholder="Module"
              options={MODULE_OPTIONS}
            />
          )}
        </FormField>
        <FormField label="Tool" required error={errors.tool}>
          {/* Dropdown rather than FormSelect: this field greys out until a
              module is picked, and the shared FormSelect wrapper takes no
              `disabled`. FormSelect is only a Dropdown with variant="form", so
              this renders identically. */}
          <Dropdown
            value={matchedToolValue}
            onChange={(v) => update("tool", v)}
            placeholder="Tool Name"
            options={activeToolOptions}
            variant="form"
            className="w-full"
            disabled={!draft.module || (toolsLoading && !isStocModule && !allowedTools && !customToolOptions)}
          />
          {toolsLoading && !isStocModule && !allowedTools && !customToolOptions && (
            <span className="text-xs text-muted-foreground">Loading tools…</span>
          )}
        </FormField>
      </div>

      {isStocModule && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Instance Name" required error={errors.instanceName}>
          <FormInput
            value={draft.instanceName ?? ""}
            onChange={(v) => update("instanceName", v)}
            placeholder="Auto-filled from environment"
            disabled
          />
        </FormField>
        <FormField label="Environment" required error={errors.environment}>
          <Dropdown
            value={draft.environment ?? ""}
            onChange={(v) => update("environment", v)}
            placeholder="Select Environment"
            options={environmentOptions}
            variant="form"
            className="w-full"
            disabled={!draft.tool}
          />
        </FormField>
      </div>
    )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Import Frequency" required error={errors.importFrequency}>
          <FormSelect
            value={draft.importFrequency}
            onChange={(v) => update("importFrequency", v)}
            placeholder="Import Frequency"
            options={FREQUENCY_OPTIONS}
          />
        </FormField>
        <FormField label="Import On" required error={errors.importOn}>
          {renderImportOn()}
        </FormField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Import At" required error={errors.importAt}>
          {/* The shared picker rather than a native <input type="time">: the
              browser's own popup cannot be themed and renders differently in
              every one of them. */}
          <DateTimePicker
            variant="time-stepper"
            value={draft.importAt}
            onValueChange={(v) => update("importAt", v ?? "")}
            placeholder="HH:MM"
          />
        </FormField>
      </div>
    </Modal>
    <DiscardConfirmDialog {...confirmProps} />
  </>
  );
}
